/* eslint-disable camelcase */
import { FC, Fragment, useCallback, useEffect, useState } from 'react'
import {
  useTranslate,
  useUpdate,
  useRefresh,
  useStore,
} from 'react-admin'
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField as MuiTextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  CircularProgress,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CloseIcon from '@mui/icons-material/Close'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { useNavigate } from 'react-router-dom'
import {
  AccessRequestOut,
  PlatformRequestOut,
  WorkflowGenerationRequestOut,
} from '../../types/apiRequests'
import { getJson, getUrl, postJson } from '../../providers/data'
import { xxllncColor } from '../../layout/theme/color'
import { border } from '../../layout/theme/border'
import { AccessStatusChip, RestrictedButton, formatDateTime } from '../../components'
import { SCOPES } from '../../constants/scopes'

type StatusFilter = 'pending' | 'approved' | 'denied' | 'all'
type TypeFilter = 'all' | 'access' | 'platform' | 'workflow' | 'workflow_generation'

interface AccessVariantBase {
  id: string
  requester_email: string
  organization_name: string | null
  organization_id: string | null
  description: string
  message: string | null
  status: string
  created: string
  raw: AccessRequestOut
}

type CombinedRequest =
  | (AccessVariantBase & { type: 'access' })
  | (AccessVariantBase & { type: 'workflow' })
  | {
    id: string
    type: 'platform'
    requester_email: string
    organization_name: string | null
    organization_id: string | null
    description: string
    message: string | null
    status: string
    created: string
    raw: PlatformRequestOut
  }
  | {
    id: string
    type: 'workflow_generation'
    requester_email: string
    organization_name: string | null
    organization_id: string | null
    description: string
    message: string | null
    status: string
    created: string
    raw: WorkflowGenerationRequestOut
  }

const isAccessRequest = (item: CombinedRequest): item is CombinedRequest & { raw: AccessRequestOut } =>
  item.type === 'access' || item.type === 'workflow'

const isWorkflowRequest = (requestType: string): boolean =>
  requestType === 'workflow_organization'

const isOrganizationRequest = (requestType: string): boolean =>
  requestType === 'organization'

const mapAccessRequest = (r: AccessRequestOut): CombinedRequest => {
  const isWorkflow = isWorkflowRequest(r.request_type)
  const name = isWorkflow
    ? (r.workflow_display_name ?? 'Workflow')
    : (r.server_display_name ?? 'MCP')
  const envSuffix = r.environment_name ? ` (${r.environment_name})` : ''

  return {
    id: r.id,
    type: isWorkflow ? 'workflow' : 'access',
    requester_email: r.requester_email,
    organization_name: r.organization_display_name ?? null,
    organization_id: r.organization_id ?? null,
    description: isWorkflow ? `Workflow: ${name}` : `MCP: ${name}${envSuffix}`,
    message: r.message ?? null,
    status: r.status,
    created: r.created,
    raw: r,
  }
}

const mapPlatformRequest = (r: PlatformRequestOut): CombinedRequest => ({
  id: r.id,
  type: 'platform',
  requester_email: r.requester_email,
  organization_name: r.organization_display_name,
  organization_id: r.organization_id,
  description: `Tier: ${r.requested_tier.charAt(0).toUpperCase()}${r.requested_tier.slice(1)}`,
  message: r.message ?? null,
  status: r.status,
  created: r.created,
  raw: r,
})

const mapWorkflowGenerationRequest = (r: WorkflowGenerationRequestOut): CombinedRequest => ({
  id: r.id,
  type: 'workflow_generation',
  requester_email: r.requester_email,
  organization_name: r.organization_display_name ?? null,
  organization_id: r.organization_id ?? null,
  description: `AI: ${r.prompt.slice(0, 80)}${r.prompt.length > 80 ? '…' : ''}`,
  message: r.summary ?? null,
  status: r.status,
  created: r.created,
  raw: r,
})

const TYPE_LABELS: Record<string, string> = {
  access: 'requests.mcpRequest',
  platform: 'requests.platformRequest',
  workflow: 'requests.workflowRequest',
  // eslint-disable-next-line camelcase
  workflow_generation: 'requests.workflowGenerationRequest',
}

const TYPE_COLORS: Record<string, string> = {
  access: xxllncColor.dark100,
  platform: xxllncColor.orange100,
  workflow: xxllncColor.purple100,
  // eslint-disable-next-line camelcase
  workflow_generation: '#6366F1',
}

const TypeChip: FC<{ type: 'access' | 'platform' | 'workflow' | 'workflow_generation' }> = ({ type }) => {
  const translate = useTranslate()
  // eslint-disable-next-line security/detect-object-injection
  const label = TYPE_LABELS[type] ?? TYPE_LABELS.access
  // eslint-disable-next-line security/detect-object-injection
  const color = TYPE_COLORS[type] ?? TYPE_COLORS.access
  return (
    <Chip
      label={translate(label)}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: 11,
        height: 22,
        border: 'none',
        bgcolor: `${color}14`,
        color,
      }}
    />
  )
}

const DetailField: FC<{ label: string; value: string | null | undefined }> = ({ label, value }) => {
  if (!value) return null
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  )
}

const ExpandedDetails: FC<{ item: CombinedRequest }> = ({ item }) => {
  const translate = useTranslate()

  if (item.type === 'workflow_generation') {
    const record = item.raw
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <DetailField label={translate('requests.aiPrompt')} value={record.prompt} />
        <DetailField label={translate('requests.aiSummary')} value={record.summary ?? null} />
        <DetailField label={translate('requests.reviewNote')} value={record.review_note} />
        <DetailField label={translate('requests.reviewedBy')} value={record.reviewed_by} />
      </Box>
    )
  }

  if (isAccessRequest(item)) {
    const record = item.raw
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {item.type === 'workflow' && record.workflow_display_name && (
          <DetailField label="Workflow" value={record.workflow_display_name} />
        )}
        <DetailField label={translate('requests.environmentUrl')} value={record.environment_url} />
        <DetailField label={translate('accessRequest.fields.environmentName')} value={record.environment_name} />
        <DetailField label={translate('accessRequest.fields.message')} value={record.message} />
        <DetailField label={translate('requests.reviewNote')} value={record.review_note} />
        <DetailField label={translate('requests.reviewedBy')} value={record.reviewed_by} />
      </Box>
    )
  }

  const record = item.raw
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
      <DetailField
        label={translate('requests.requestedTier')}
        value={record.requested_tier ? record.requested_tier.charAt(0).toUpperCase() + record.requested_tier.slice(1) : ''}
      />
      <DetailField label={translate('accessRequest.fields.message')} value={record.message} />
      <DetailField label={translate('requests.reviewNote')} value={record.review_note} />
      <DetailField label={translate('requests.reviewedBy')} value={record.reviewed_by} />
    </Box>
  )
}

const DenyDialog: FC<{
  open: boolean
  onClose: () => void
  onDeny: (note: string) => void
}> = ({ open, onClose, onDeny }) => {
  const translate = useTranslate()
  const [note, setNote] = useState('')

  const handleDeny = () => {
    onDeny(note)
    setNote('')
  }

  const handleClose = () => {
    onClose()
    setNote('')
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{translate('accessRequest.denyDialog.title')}</DialogTitle>
      <DialogContent>
        <MuiTextField
          fullWidth
          label={translate('accessRequest.denyDialog.reason')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          multiline
          rows={3}
          sx={{ mt: 1 }}
          required
          inputProps={{ maxLength: 2000 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {translate('accessRequest.denyDialog.cancel')}
        </Button>
        <Button onClick={handleDeny} color="error" disabled={!note.trim()}>
          {translate('accessRequest.deny')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const toggleButtonGroupSx = {
  '& .MuiToggleButton-root': {
    'textTransform': 'none' as const,
    'fontWeight': 600,
    'fontSize': 11,
    'px': 1.5,
    'py': 0.25,
    'border': '1px solid rgba(0,0,0,0.08)',
    'color': 'rgba(0,0,0,0.4)',
    '&.Mui-selected': {
      'bgcolor': xxllncColor.opDark10,
      'color': xxllncColor.dark100,
      'borderColor': xxllncColor.opDark20,
      '&:hover': { bgcolor: xxllncColor.opDark20 },
    },
  },
}

const RequestList: FC = () => {
  const translate = useTranslate()
  const navigate = useNavigate()
  const [update] = useUpdate()
  const refresh = useRefresh()
  const [refreshVersion] = useStore('RaStore.refresh', 0)
  const [requests, setRequests] = useState<CombinedRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [denyTarget, setDenyTarget] = useState<CombinedRequest | null>(null)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending')
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const fetchRequests = useCallback(async () => {
    setLoading(true)
    try {
      const baseUrl = getUrl()
      const filter = statusFilter === 'all'
        ? {}
        : { status: statusFilter }
      const accessFilter = encodeURIComponent(JSON.stringify(filter))
      const sort = statusFilter === 'pending'
        ? encodeURIComponent(JSON.stringify(['created', 'ASC']))
        : encodeURIComponent(JSON.stringify(['created', 'DESC']))
      const range = encodeURIComponent(JSON.stringify([0, 99]))

      const [accessData, platformData, workflowGenData] = await Promise.all([
        getJson<AccessRequestOut[]>(
          `${baseUrl}/access_request?filter=${accessFilter}&sort=${sort}&range=${range}`,
        ),
        getJson<PlatformRequestOut[]>(
          `${baseUrl}/platform_request?filter=${accessFilter}&sort=${sort}&range=${range}`,
        ),
        getJson<WorkflowGenerationRequestOut[]>(
          `${baseUrl}/workflow_generation_request?filter=${accessFilter}&sort=${sort}&range=${range}`,
        ),
      ])

      const combined = [
        ...accessData.map(mapAccessRequest),
        ...platformData.map(mapPlatformRequest),
        ...workflowGenData.map(mapWorkflowGenerationRequest),
      ].sort((a, b) => {
        if (statusFilter === 'pending') {
          return new Date(a.created).getTime() - new Date(b.created).getTime()
        }
        return new Date(b.created).getTime() - new Date(a.created).getTime()
      })

      setRequests(combined)
    } catch {
      setRequests([])
    } finally {
      setLoading(false)
    }
  }, [statusFilter])

  useEffect(() => {
    void fetchRequests()
  }, [fetchRequests, refreshVersion])

  useEffect(() => {
    setExpandedId(null)
  }, [statusFilter, typeFilter])

  const filteredRequests = typeFilter === 'all'
    ? requests
    : requests.filter((r) => r.type === typeFilter)

  const handleApproveAccess = (item: CombinedRequest & { type: 'access' | 'workflow' }) => {
    const record = item.raw
    if (isOrganizationRequest(record.request_type)) {
      const prefill: Record<string, string> = {
        organization_id: record.organization_id ?? '',
        server_id: record.server_id ?? '',
      }
      if (record.environment_url) {
        prefill.base_url = record.environment_url
      }
      if (record.environment_id) {
        prefill.environment_id = record.environment_id
      }
      void update('access_request', {
        id: record.id, data: {}, meta: { action: 'approve' },
      }, {
        mutationMode: 'pessimistic',
        onSuccess: () => {
          navigate('/organization_mcp_link/create', { state: { record: prefill } })
        },
      })
    } else {
      void update(
        'access_request',
        { id: record.id, data: {}, meta: { action: 'approve' } },
        {
          mutationMode: 'pessimistic',
          onSuccess: () => {
            refresh()
            void fetchRequests()
          },
        },
      )
    }
  }

  const handleApprovePlatform = (item: CombinedRequest) => {
    void update(
      'platform_request',
      { id: item.id, data: {}, meta: { action: 'approve' } },
      {
        mutationMode: 'pessimistic',
        onSuccess: () => {
          refresh()
          void fetchRequests()
        },
      },
    )
  }

  const handleApproveWorkflow = (item: CombinedRequest & { type: 'workflow' }) => {
    const record = item.raw
    void update(
      'access_request',
      { id: record.id, data: {}, meta: { action: 'approve' } },
      {
        mutationMode: 'pessimistic',
        onSuccess: () => {
          // Backend approval auto-creates the link, copies server_configs/attachment, and grants the user — no /create navigation needed.
          refresh()
          void fetchRequests()
        },
      },
    )
  }

  const handleApproveWorkflowGeneration = async (
    item: CombinedRequest & { type: 'workflow_generation' },
  ) => {
    try {
      await postJson(`${getUrl()}/workflow_generation_request/${item.id}/approve`, {})
      refresh()
      void fetchRequests()
      navigate(`/workflow?ai_request=${item.id}`)
    } catch {
      refresh()
      void fetchRequests()
    }
  }

  const handleApprove = (item: CombinedRequest) => {
    if (item.type === 'access') {
      handleApproveAccess(item)
    } else if (item.type === 'workflow') {
      handleApproveWorkflow(item)
    } else if (item.type === 'workflow_generation') {
      void handleApproveWorkflowGeneration(item)
    } else {
      handleApprovePlatform(item)
    }
  }

  const handleDeny = async (note: string) => {
    if (!denyTarget) return
    if (denyTarget.type === 'workflow_generation') {
      try {
        await postJson(
          `${getUrl()}/workflow_generation_request/${denyTarget.id}/deny`,
          { review_note: note },
        )
      } finally {
        setDenyTarget(null)
        refresh()
        void fetchRequests()
      }
      return
    }
    const resource = denyTarget.type === 'platform' ? 'platform_request' : 'access_request'
    void update(
      resource,
      {
        id: denyTarget.id,
        data: {
          review_note: note,
        },
        meta: { action: 'deny' },
      },
      {
        mutationMode: 'pessimistic',
        onSuccess: () => {
          setDenyTarget(null)
          refresh()
          void fetchRequests()
        },
      },
    )
  }

  const getApproveLabel = (item: CombinedRequest): string => {
    if (item.type === 'platform') return translate('platformRequest.approve')
    if (item.type === 'workflow_generation') return translate('requests.openInGenerator')
    const record = item.raw
    return isOrganizationRequest(record.request_type)
      ? translate('accessRequest.createLink')
      : translate('accessRequest.approve')
  }

  const toggleExpand = (itemKey: string) => {
    setExpandedId((prev) => (prev === itemKey ? null : itemKey))
  }

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{
        display: 'flex',
        gap: 2,
        mb: 2,
        flexWrap: 'wrap',
        alignItems: 'center',
      }}>
        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          onChange={(_, v: StatusFilter | null) => { if (v) setStatusFilter(v) }}
          size="small"
          sx={toggleButtonGroupSx}
        >
          <ToggleButton value="pending">{translate('requests.filterPending')}</ToggleButton>
          <ToggleButton value="approved">{translate('requests.filterApproved')}</ToggleButton>
          <ToggleButton value="denied">{translate('requests.filterDenied')}</ToggleButton>
          <ToggleButton value="all">{translate('requests.filterAll')}</ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          value={typeFilter}
          exclusive
          onChange={(_, v: TypeFilter | null) => { if (v) setTypeFilter(v) }}
          size="small"
          sx={toggleButtonGroupSx}
        >
          <ToggleButton value="all">{translate('requests.filterAll')}</ToggleButton>
          <ToggleButton value="access">{translate('requests.mcpRequest')}</ToggleButton>
          <ToggleButton value="workflow">{translate('requests.workflowRequest')}</ToggleButton>
          <ToggleButton value="platform">{translate('requests.platformRequest')}</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {filteredRequests.length === 0 ? (
        <Box sx={{
          background: '#fff',
          borderRadius: border.radius.lg + 'px',
          border: '1px solid rgba(0,0,0,0.06)',
          p: 6,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 48, color: xxllncColor.green100, opacity: 0.6 }} />
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            {translate('requests.noRequests')}
          </Typography>
        </Box>
      ) : (
        <TableContainer sx={{
          background: '#fff',
          borderRadius: border.radius.lg + 'px',
          border: '1px solid rgba(0,0,0,0.06)',
        }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>
                  {translate('accessRequest.fields.type')}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>
                  {translate('accessRequest.fields.requester')}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>
                  {translate('accessRequest.fields.organization')}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>
                  {translate('data.description')}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>
                  {translate('accessRequest.fields.date')}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>
                  {translate('filters.status')}
                </TableCell>
                {statusFilter === 'pending' && (
                  <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>
                    {translate('data.actions')}
                  </TableCell>
                )}
                <TableCell sx={{ width: 40 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRequests.map((item) => {
                const itemKey = `${item.type}-${item.id}`
                const isExpanded = expandedId === itemKey
                const isPending = item.status === 'pending'
                return (
                  <Fragment key={itemKey}>
                    <TableRow
                      hover
                      onClick={() => toggleExpand(itemKey)}
                      sx={{ 'cursor': 'pointer', '& > *': { borderBottom: isExpanded ? 'none' : undefined } }}
                    >
                      <TableCell>
                        <TypeChip type={item.type} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{item.requester_email}</Typography>
                      </TableCell>
                      <TableCell>
                        {item.organization_id ? (
                          <Typography
                            variant="body2"
                            onClick={(e) => {
                              e.stopPropagation()
                              navigate(`/organization/${item.organization_id}/show`)
                            }}
                            sx={{
                              'cursor': 'pointer',
                              'color': xxllncColor.blue100,
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            {item.organization_name}
                          </Typography>
                        ) : (
                          <Typography variant="body2">{item.organization_name ?? '-'}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{item.description}</Typography>
                        {item.message && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                            {item.message}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatDateTime(item.created)}</Typography>
                      </TableCell>
                      <TableCell>
                        <AccessStatusChip status={item.status} />
                      </TableCell>
                      {statusFilter === 'pending' && (
                        <TableCell>
                          {isPending && (
                            <RestrictedButton access={[
                              item.type === 'access'
                                ? SCOPES.ACCESS_REQUEST_WRITE
                                : SCOPES.PLATFORM_REQUEST_WRITE,
                            ]}>
                              <Box sx={{ display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                                <Button
                                  size="small"
                                  color="success"
                                  startIcon={<CheckIcon />}
                                  onClick={() => handleApprove(item)}
                                  sx={{ gap: 0.5 }}
                                >
                                  {getApproveLabel(item)}
                                </Button>
                                <Button
                                  size="small"
                                  color="error"
                                  startIcon={<CloseIcon />}
                                  onClick={() => setDenyTarget(item)}
                                  sx={{ gap: 0.5 }}
                                >
                                  {translate('accessRequest.deny')}
                                </Button>
                              </Box>
                            </RestrictedButton>
                          )}
                        </TableCell>
                      )}
                      <TableCell sx={{ width: 40 }}>
                        {isExpanded
                          ? <KeyboardArrowUpIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                          : <KeyboardArrowDownIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                        }
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={statusFilter === 'pending' ? 8 : 7}
                        sx={{ py: 0, px: 0, borderBottom: isExpanded ? undefined : 'none' }}
                      >
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{
                            px: 3,
                            py: 2,
                            bgcolor: 'rgba(0,0,0,0.015)',
                          }}>
                            <ExpandedDetails item={item} />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <DenyDialog
        open={denyTarget !== null}
        onClose={() => setDenyTarget(null)}
        onDeny={(note) => { void handleDeny(note) }}
      />
    </Box>
  )
}

export default RequestList
